// @flow

import React, { Component } from 'react';
import _ from 'lodash'

import I18n from 'PMT/i18n/I18n';

// -- constants
import Events from 'constants/Events';
import RoutingEnum from 'constants/RoutingEnum';

// -- utils
import {
  ViewUtils,
} from 'PMT/utils';
import Listener from 'PMT/flux/Listener';

import SecurityContext from 'utils/permissions/SecurityContext';

// -- actions
import { RestaurantMenuAction, RouteAction } from 'actions';

// -- stores
import SessionStore from 'stores/SessionStore';
import {RestaurantMenuStore, OrderSettingsStore, RestaurantStore} from 'stores';

// -- entities
import Restaurant from 'PMT/entities/Restaurant';
import {
  Menu,
  RestaurantMenu,
} from 'PMT/entities/menu';

import { OrderSettings } from 'PMT/entities/order';

// -- components
import RestaurantMenuPage from '../RestaurantMenuPage';
import AbstractComponent from 'components/AbstractComponent';
import Ariane from 'PMT/components/Ariane';
import MenuEditView from './MenuEditView';

export default class MenuEditPage extends AbstractComponent {

  state = {
    restaurantMenu: RestaurantMenuStore.getRestaurantMenu(),
    restaurant: RestaurantStore.getRestaurant(),
    menu: this.isEditMode() ? null : new Menu(),
    apiError: null
  };

  componentDidMount() {
    RestaurantMenuStore.addListeners(this.restaurantMenuStoreListeners);

    if (!SecurityContext.restaurantMenu.canEdit) {
      // TODO: redirect
    }

    if (_.isNull(this.state.restaurantMenu)) {
      RestaurantMenuAction.loadRestaurantMenu();
    } else if (this.isEditMode()) {
      this.loadMenu();
    }
  }

  componentWillUnmount() {
    RestaurantMenuStore.removeListeners(this.restaurantMenuStoreListeners);
  }

  get restaurantMenuStoreListeners(): Array<Listener> {
    return [
      new Listener(Events.ON_LOADING_RESTAURANT_MENU_SUCCESS, this.onChange),
      new Listener(Events.ON_LOADING_RESTAURANT_MENU_ERROR, this.onError),

      new Listener(Events.ON_UPDATE_RESTAURANT_MENU_SUCCESS, this.onMenuUpdated),
      new Listener(Events.ON_UPDATE_RESTAURANT_MENU_ERROR, this.onError),

      new Listener(Events.ON_CREATE_RESTAURANT_MENU_MENU_SUCCESS, this.onProductOptionCreated),
      new Listener(Events.ON_CREATE_RESTAURANT_MENU_MENU_ERROR, this.onError),

      new Listener(Events.ON_DELETE_RESTAURANT_MENU_MENU_SUCCESS, this.onProductOptionDeleted),
      new Listener(Events.ON_DELETE_RESTAURANT_MENU_MENU_ERROR, this.onError),

      new Listener(Events.ON_UPDATE_RESTAURANT_MENU_MENU_SUCCESS, this.onMenuUpdated),
      new Listener(Events.ON_UPDATE_RESTAURANT_MENU_MENU_ERROR, this.onError),

      new Listener(Events.ON_DELETE_RESTAURANT_MENU_MENU_IMAGE_SUCCESS, this.onMenuUpdated),
      new Listener(Events.ON_DELETE_RESTAURANT_MENU_MENU_IMAGE_ERROR, this.onError)
    ];
  }

  loadMenu() {
    const menu: Menu = this.getMenuOnRestaurantMenu(this.state.restaurantMenu);

    // TODO: if menu === null redirect to 404.
    this.setState({
      menu,
      loading: false
    });
  }

  // -- Event functions

  onChange = () => {
    const restaurantMenu:RestaurantMenu = RestaurantMenuStore.getRestaurantMenu();

    if (this.isEditMode()) {
      const menu:Menu = this.getMenuOnRestaurantMenu(restaurantMenu);
      this.setState({
        restaurantMenu,
        menu,
        loading: false
      });
    } else {
      this.setState({
        restaurantMenu,
        loading: false
      });
    }
  };

  onMenuUpdated = () => {
    if (this.isEditMode()) {
      const restaurantMenu: RestaurantMenu = RestaurantMenuStore.getRestaurantMenu();
      const menu: Menu = this.getMenuOnRestaurantMenu(restaurantMenu);

      this.setState({
        restaurantMenu,
        menu,
        loading: false
      });
    }
  };

  onProductOptionDeleted = () => {
    RouteAction.presentMenusTab();
  };

  onProductOptionCreated = () => {
    RouteAction.presentMenuDetail(RestaurantMenuStore.getCreatedMenu());
  };

  onError = () => {
    this.setState({
      apiError: RestaurantMenuStore.getApiError()
    });
  };

  onUpdateMenu = (menu: Menu, image) => {
    this.showLoading();
    const imageFile = _.isNull(image) ? null : ViewUtils.dataUriToFile(image);
    RestaurantMenuAction.saveMenu(menu, imageFile);
  };

  onImageRemove = (menu: Menu) => {
    this.showLoading();
    RestaurantMenuAction.deleteMenuImage(menu);
  }

  onCreateMenu = (menu: Menu, image) => {
    this.showLoading();
    const imageFile = _.isNull(image) ? null : ViewUtils.dataUriToFile(image);
    RestaurantMenuAction.createMenu(menu, imageFile);
  };

  onDeleteMenu = (menu: Menu) => {
    this.showLoading();
    RestaurantMenuAction.deleteMenu(menu);
  };

  get menu(): Menu {
    return this.state.menu;
  }

  get restaurantMenu(): RestaurantMenu {
    return this.state.restaurantMenu;
  }

  get restaurant(): Restaurant {
    return this.state.restaurant;
  }

  isEditMode() {
    return !_.isUndefined(this.props.params.menuId);
  }

  getMenuOnRestaurantMenu(restaurantMenu) {
    const menuId = this.props.params.menuId;
    const menu: Menu = restaurantMenu.getMenuWithId(menuId);

    return menu;
  }


  // -- renderers

  renderBody() {
    if (!_.isNull(this.apiError)) {
      return this.renderApiErrorBlock();
    } else if (_.isNull(this.menu) || _.isNull(this.restaurantMenu)) {
      return this.renderLoading(true);
    }

    const arianeIcon = 'fa-shopping-cart';

    const arianeDetails = [
      {
        name: I18n.tr('menu.order'),
        link: null
      },
      {
        name: I18n.tr('menu.order.restaurant_menu'),
        link: RoutingEnum.RESTAURANT_MENU,
        query: {
          tab: RestaurantMenuPage.Tabs.CATEGORIES
        }
      },
      {
        name: I18n.tr('menu.order.restaurant_menu.menus'),
        link: RoutingEnum.RESTAURANT_MENU,
        query: {
          tab: RestaurantMenuPage.Tabs.MENUS
        }
      }
    ];

    if (this.isEditMode()) {
      arianeDetails.push({
        name: this.menu.name,
        link: RoutingEnum.RESTAURANT_MENU_MENU_DETAIL,
        params: {
          menuId: this.menu.id
        }
      });
      arianeDetails.push({
        name: I18n.tr('menu.order.restaurant_menu.edition'),
        link: null
      });
    } else {
      arianeDetails.push({
        name: I18n.tr('menu.order.restaurant_menu.new'),
        link: null
      });
    }

    return (
      <div>
        <Ariane icon={arianeIcon} details={arianeDetails} />

        <MenuEditView
          menu={this.menu}
          restaurantMenu={this.restaurantMenu}
          onUpdate={this.onUpdateMenu}
          onImageRemove={this.onImageRemove}
          onCreate={this.onCreateMenu}
          onDelete={this.onDeleteMenu}
          isEditMode={this.isEditMode()}
          orderSettings={OrderSettingsStore.getOrderSettings()}
          currencyCode={this.state.restaurant.currency}
        />
      </div>
    );
  }

  render() {
    try {
      return (
        <div className="menu_detail_page">
          {this.renderBody()}
          {this.renderLoading()}
        </div>
      );
    } catch (e) { console.error(e); }
  }

}



// WEBPACK FOOTER //
// ./scripts/pages/menu/menuEdit/MenuEditPage.js